import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EXTERNAL_ACTION_TYPES, getActionUrl } from '../constants/externalConfig';
import { useSubmitScholarProgramPreferencesMutation } from '../../services/graphql/api';

/**
 * Custom hook for managing Student Dashboard actions
 * Centralizes all action handling logic
 */
export const useStudentDashboardActions = (refetch, setShowPreferencesForm) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [submitPreferences, { isLoading: isSubmittingPreferences }] = useSubmitScholarProgramPreferencesMutation();

  const handleStepAction = useCallback(async (actionType, actionData) => {
    // Handle accept invitation action
    if (actionType === EXTERNAL_ACTION_TYPES.ACCEPT_INVITATION) {
      // Check legacy scholars setting directly
      const isLegacyEnabled = process.env.REACT_APP_ENABLE_LEGACY_SCHOLARS === 'true';
      
      if (!isLegacyEnabled) {
        // For modern scholars flow, show preferences form internally
        // Stay in SCHOLARS_INVITATION flow, just show the form
        setShowPreferencesForm(true);
        return;
      } else {
        // For legacy scholars flow, redirect to external URL
        const legacyUrl = process.env.REACT_APP_LEGACY_SCHOLARS_URL || 'https://hsapp-dev.webtest.osu.edu/HonorsAndScholars/Secure/Confirmation.aspx';
        window.open(legacyUrl, '_blank', 'noopener,noreferrer');
        return;
      }
    }

    // Handle other external link actions (but not ACCEPT_INVITATION which is handled above)
    if (Object.values(EXTERNAL_ACTION_TYPES).includes(actionType) && actionType !== EXTERNAL_ACTION_TYPES.ACCEPT_INVITATION) {
      const url = getActionUrl(actionType);
      if (url && url !== '#') {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
    }
    
    // Handle other action types
    switch (actionType) {
      case "explore":
        // Legacy support - redirect to scholars program
        window.open("https://honors-scholars.osu.edu/scholars", "_blank", "noopener,noreferrer");
        break;
      case "complete-application":
        window.open("https://www.osu.edu", "_blank", "noopener,noreferrer");
        break;
      case "contact":
        window.open("https://honors-scholars.osu.edu/contact", "_blank", "noopener,noreferrer");
        break;
      case "info":
        window.open("https://honors-scholars.osu.edu/honors", "_blank", "noopener,noreferrer");
        break;
      case "accept-scholars":
        // Accept Scholars offer - would trigger mutation
        break;
      case "decline-scholars":
        // Decline Scholars offer - would trigger mutation
        break;
      case "submit-preferences":
        try {
          // Extract emplid and preferences from actionData
          const { emplid, preferences } = actionData;
          
          if (!emplid) {
            throw new Error('Employee ID is required for preferences submission');
          }
          
          await submitPreferences({ emplid, preferences });
          
          // Handle success - refetch data to get updated state
          if (refetch) {
            refetch();
          }
          
          // Hide preferences form after successful submission
          if (setShowPreferencesForm) {
            setShowPreferencesForm(false);
          }
        } catch (error) {
          console.error("❌ V2 Failed to submit preferences:", error);
          // TODO: Show error message to user
        }
        break;
      case "cancel-preferences":
        // Handle preferences cancellation
        break;
      default:
        // Unknown action type
    }
  }, [searchParams, setSearchParams, submitPreferences, refetch, setShowPreferencesForm]);

  const handleScenarioChange = useCallback((scenarioKey) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("scenario", scenarioKey);
    setSearchParams(newParams);
    
    // Refetch data after scenario change
    if (refetch) {
      refetch();
    }
  }, [searchParams, setSearchParams, refetch]);

  return {
    handleStepAction,
    handleScenarioChange,
    isSubmittingPreferences
  };
};

export default useStudentDashboardActions;